import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/Tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import { Label } from "../ui/Label";
import { Button } from "../ui/Button";
import { EditOption } from "../../api/edits/editRoute.responses";
import useEditRouteService from "../../api/edits/editRoute.service";
import { Combobox } from "../ui/Combobox";
import { useNavigate } from "react-router-dom";

interface LiveOrEditRouteTabProps {
  handleBackClick: () => void;
}

function LiveOrEditRouteTab({ handleBackClick }: LiveOrEditRouteTabProps) {
  const [routesInEditing, setRoutesInEditing] = useState<EditOption[]>([]);
  const [routesLive, setRoutesLive] = useState<EditOption[]>([]);
  const [selectedRouteToEdit, setSelectedRouteToEdit] =
    useState<EditOption | null>(null);
  const [isLiveOptionSelected, setIsLiveOptionSelected] =
    useState<boolean>(true);

  const { editRoute, getEditOptions } = useEditRouteService();

  const navigate = useNavigate();

  useEffect(() => {
    //self executing function so there is no redundant function declaration
    (async () => {
      try {
        const editingOptions = (await getEditOptions()).data;
        setRoutesInEditing(editingOptions.currentlyEditing);
        setRoutesLive(editingOptions.currentlyLive);
      } catch (error) {
        alert(error);
      }
    })();
  }, []);

  const handleLiveLoad = async () => {
    try {
      //move the route to edit
      if (selectedRouteToEdit) {
        const response = await editRoute(selectedRouteToEdit.id);

        if (response.status === 201) navigate(`/${selectedRouteToEdit.id}`);
      }
    } catch (error) {}
  };

  const handleEditLoad = () => {
    if (selectedRouteToEdit) navigate(`/${selectedRouteToEdit.id}`);
  };

  return (
    <Tabs
      defaultValue="edit"
      className="w-[400px]"
      onValueChange={() => setSelectedRouteToEdit(null)}
    >
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="edit">Edit</TabsTrigger>
        <TabsTrigger value="live">Live</TabsTrigger>
      </TabsList>
      <TabsContent value="edit">
        <Card>
          <CardHeader>
            <CardTitle>Load route in editing</CardTitle>
            <CardDescription>
              Edit a route that is already in editing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Route name</Label>
              <Combobox<EditOption>
                label="route"
                options={routesInEditing}
                selectedOption={selectedRouteToEdit}
                onOptionChange={setSelectedRouteToEdit}
                getOptionLabel={(option: EditOption) => option.name}
                getOptionValue={(option: EditOption) => option.id}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant={"outline"} onClick={handleBackClick}>
              Back
            </Button>
            <Button onClick={handleEditLoad}>Load</Button>
          </CardFooter>
        </Card>
      </TabsContent>
      <TabsContent value="live">
        <Card>
          <CardHeader>
            <CardTitle>Load live route</CardTitle>
            <CardDescription>Move the route from live to edit.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="current">Route name</Label>
              <Combobox<EditOption>
                label="route"
                options={routesLive}
                selectedOption={selectedRouteToEdit}
                onOptionChange={setSelectedRouteToEdit}
                getOptionLabel={(option: EditOption) => option.name}
                getOptionValue={(option: EditOption) => option.id}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant={"outline"} onClick={handleBackClick}>
              Back
            </Button>
            <Button onClick={handleLiveLoad}>Load</Button>
          </CardFooter>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

export default LiveOrEditRouteTab;
